/* eslint-disable no-console */
import {
  favorite as PrismaFavorite,
} from '@prisma/client'
import axios from 'axios'
import { useEffect, useRef, useState } from 'react'
import config from '../../../config'
import { googleTag } from '../../utils/googleTagManager'

export enum EnumUseFavoritesSuccess {
  Add,
  Remove,
}

export enum EnumUseFavoritesError {
  Add,
  Remove,
  InitialFetch,
}

type LoadingRecord = Record<string, boolean>


type UseFavorites = (arg: {
  onSuccess: (type: EnumUseFavoritesSuccess) => void
  onError: (type: EnumUseFavoritesError) => void
  favoritesAsArg?: PrismaFavorite[]
}) => {
  favorites: string[]
  onFavorites: (eventId: string, eventName: string) => void
  loading: boolean
}


export const useFavorites: UseFavorites = ({
  onError,
  onSuccess,
  favoritesAsArg,
}) => {
  const [favorites, setFavorites] = useState<string[] | undefined>(favoritesAsArg ? favoritesAsArg.map((favorite) => favorite.eventId) : undefined)

  // We need imidiate feedback to prevent race condition.
  // React state is async refs are not.
  const [loading, setLoading] = useState<boolean>(!favoritesAsArg) // No loading if provided as argument
  const refLoadingFavorite = useRef<LoadingRecord>({})
  const handleChangeLoading = (eventId: string, loading: boolean) => {
    refLoadingFavorite.current[eventId] = loading
  }

  useEffect(() => {
    // When favorites is already provided we can safe an api call

    if (!favorites && config.USE_FAVORITES) {
      axios.get<PrismaFavorite[]>('/api/favorites')
        .then((response) => {
          setFavorites(response.data.map((favorite) => favorite.eventId))
          setLoading(false)
        })
        .catch((error) => {
          onError(EnumUseFavoritesError.InitialFetch)
          console.error(error)
          throw Error(`favorites error - ${error}`)
        })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const favoriteRemove = (eventId: string, index: number) => {
    if (!favorites) {
      return
    }
    setFavorites([...favorites.slice(0, index), ...favorites.slice(index + 1)])

    return axios.delete<PrismaFavorite[]>(`/api/favorites/${eventId}`)
      .then(() => {
        onSuccess(EnumUseFavoritesSuccess.Remove)
      })
      .catch((error) => {
        console.error(error)
        setFavorites([...favorites, eventId])
        onError(EnumUseFavoritesError.Remove)
      })
      .finally(() => {
        handleChangeLoading(eventId, false)
      })
  }

  const favoriteAdd = (eventId: string, eventName: string) => {
    if (!favorites) {
      return
    }

    googleTag.event({
      event: 'buttonClick',
      category: 'hugverk',
      action: 'favorites',
      label: eventName,
    })

    setFavorites([...favorites, eventId])
    return axios.post<PrismaFavorite[]>('/api/favorites', { eventId })
      .then(() => {
        onSuccess(EnumUseFavoritesSuccess.Add)
      })
      .catch((error) => {
        console.error(error)
        onError(EnumUseFavoritesError.Add)
        const index = favorites.findIndex((x) => x === eventId)
        setFavorites([...favorites.slice(0, index), ...favorites.slice(index + 1)])
      })
      .finally(() => {
        handleChangeLoading(eventId, false)
      })
  }

  const handleFavorites = (eventId: string, eventName: string) => {
    if (!favorites) {
      return
    }
    // We dont want race conditions.
    if (refLoadingFavorite.current[eventId]) {
      return
    }

    handleChangeLoading(eventId, true)

    const index = favorites.findIndex((favoriteId) => favoriteId === eventId)
    const exists = index !== -1

    if (exists) {
      favoriteRemove(eventId, index)
    }
    else {
      favoriteAdd(eventId, eventName)
    }
  }

  // Cant do crud operation on an unpopulated favorites array.
  if (loading) {
    return {
      favorites: [],
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      onFavorites: ():void => { },
      loading,
    }
  }

  return {
    favorites: favorites || [],
    onFavorites: handleFavorites,
    loading,
  }
}
